import crypto from "crypto";
import { SubscriptionPlan } from "enums/subscription-plans";
import { getFilterParams } from "helpers/search";
import IntlMessageFormat from "intl-messageformat";
import Stripe from "stripe";
import {
  AskPaperThreadMessage,
  AskPaperThreadRole,
  getCustomerSubscriptionAPI,
  getCustomerUpcomingInvoiceAPI,
  getSubscriptionProductsAPI,
  getTeamSubscriptionAPI,
  UsageData,
} from "./api";
import { IClerkPublicMetaData } from "./clerk";
import { sortProducts } from "./products";
import { StripePrice } from "features/Pricing/types";

export interface SubscriptionUsageSummary {
  creditLeft: number;
  refreshDate: string;
}

export interface UserSubscription {
  cancel_at_period_end?: boolean;
  cancel_at?: number | null;
  current_period_end: number;
  customer: string;
  default_payment_method?: string;
  id: string;
  plan: {
    amount_decimal: string;
    amount: number;
    id: string;
    interval: string;
    product: string;
    metadata?: { type: string };
    tiers_mode?: string | null;
  };
  status: string;
  trial_end?: number | null;
  quantity?: number;
  items?: { data: { id: string; price: StripePrice }[] };
  discount?: {
    coupon: {
      name: string;
      percent_off: number;
    };
    end?: number;
  };
  pause_collection?: {
    behavior: string;
    resumes_at: number;
  };
}

export interface OrganizationSubscription {
  orgName: string;
}

export interface ISubscriptionData {
  user: UserSubscription | null;
  org: OrganizationSubscription | null;
  team?: boolean | null;
}

export const getActiveSubscription = async ({
  customerId,
  emailAddresses,
  headerData,
}: {
  customerId?: string;
  emailAddresses?: string[];
  headerData?: any;
}): Promise<ISubscriptionData> => {
  if (!customerId && (!emailAddresses || !emailAddresses.length)) {
    // Return an empty ISubscriptionData object if no identifying params
    // are available.
    return {
      user: null,
      org: null,
      team: null,
    };
  }

  let subscriptionRes = await getCustomerSubscriptionAPI(
    {
      stripe_customer_id: customerId,
      email_addresses: emailAddresses,
    },
    headerData
  );

  let organizationSubscription: OrganizationSubscription | null = null;
  if (subscriptionRes && subscriptionRes.org_name) {
    organizationSubscription = { orgName: subscriptionRes.org_name };
  }

  let subscription = null;
  try {
    subscription = subscriptionRes.user_subscription || null;
    subscription = subscription ? JSON.parse(subscription!) : null;
    if (subscription && !isSubscriptionActive(subscription)) {
      subscription = null;
    }
  } catch (error) {
    subscription = null;
  }

  let userSubscription: UserSubscription | null = null;
  if (subscription && subscription.plan.tiers_mode === "volume") {
    userSubscription = subscription;
  } else if (subscription && subscription.plan.tiers_mode == null) {
    userSubscription = subscription;
  }

  return {
    user: userSubscription,
    team: subscriptionRes.team,
    org: organizationSubscription,
  };
};

export const getTeamSubscriptionActiveStatus = async (teamId: string) => {
  const teamSubscription = await getTeamSubscriptionAPI(teamId);
  if (teamSubscription && teamSubscription.subscription) {
    const stripeSubJson = JSON.parse(teamSubscription.subscription);
    if (
      isSubscriptionTeams(stripeSubJson as Stripe.Subscription) &&
      isSubscriptionActive(stripeSubJson)
    ) {
      return { active: true };
    }
  }
  return { active: false };
};

export const getCustomerUpcomingInvoice = async (
  customerId?: string | null
) => {
  if (!customerId) {
    return null;
  }
  const upcomingInvoice = await getCustomerUpcomingInvoiceAPI(customerId);
  return upcomingInvoice;
};

export const getSubscriptionProducts = async () => {
  let sortedProducts = null;
  const subscriptionProducts = await getSubscriptionProductsAPI("");
  if (subscriptionProducts) {
    sortedProducts = sortProducts(
      subscriptionProducts["subscription_products"]
    );
  }
  return sortedProducts;
};

export function getCancellationExpiryDate(
  userSubscription: UserSubscription | null
): Date | null {
  if (
    userSubscription &&
    ["active", "trialing"].includes(userSubscription.status) &&
    userSubscription.cancel_at_period_end &&
    userSubscription.cancel_at
  ) {
    return new Date(userSubscription.cancel_at * 1000);
  }
  return null;
}

export function getPremiumProduct(products: any) {
  for (let i = 0; i < products.length; i++) {
    const productData = products[i].product_data;
    if (productData.metadata.tier == "premium") {
      return productData;
    }
  }
  return null;
}

// Return the total number of ask paper responses that have been generated
// since the last time the user's credits were reset.
export function calculateAskPaperMessageCountForPaper(
  messages: AskPaperThreadMessage[],
  lastResetDate?: string
): number {
  const cutoffDate = lastResetDate ? new Date(lastResetDate) : new Date();

  const count = messages.filter((message) => {
    if (message.role !== AskPaperThreadRole.ASSISTANT) return false;

    const messageDate = new Date(message.created_at);
    return messageDate > cutoffDate;
  }).length;

  return count;
}

/**
 * Ensures that the usage data is not null.
 * If it is null, we return a default usage data object.
 */
export function ensureUsageData(usage?: UsageData): UsageData {
  return {
    last_reset_date: usage?.last_reset_date,
    ask_paper_messages_per_paper_count:
      usage?.ask_paper_messages_per_paper_count ?? {},
    used_study_paper_ids: usage?.used_study_paper_ids ?? [],
    used_consensus_snapshot_queries:
      usage?.used_consensus_snapshot_queries ?? [],
    used_quick_search_queries: usage?.used_quick_search_queries ?? [],
    used_pro_search_queries: usage?.used_pro_search_queries ?? [],
    used_deep_search_queries: usage?.used_deep_search_queries ?? [],
    bonus_deep_search_credits: usage?.bonus_deep_search_credits ?? 0,
  };
}

export function convertSubscriptionDataToPartialUserMetadata(
  subscription: ISubscriptionData
): Partial<IClerkPublicMetaData> {
  return {
    is_premium: isSubscriptionPremium(subscription),
  };
}

export function getCreditRefreshDate(usageData: UsageData): string {
  let refreshDate = usageData.last_reset_date
    ? new Date(usageData.last_reset_date)
    : new Date();
  refreshDate.setDate(refreshDate.getDate() + 30);

  const day = refreshDate.getDate();
  const month = refreshDate.toLocaleDateString("en-US", { month: "long" });

  const ordinalFormat = new IntlMessageFormat(
    "{day, selectordinal, one {#st} two {#nd} few {#rd} other {#th}}",
    "en"
  );
  const dayWithSuffix = ordinalFormat.format({ day });

  return `${month} ${dayWithSuffix}`;
}

export function hashQuery(query: { [key: string]: any }) {
  const fullQuery = (query.q as string) + getFilterParams(query);

  const queryHash = crypto.createHash("md5").update(fullQuery).digest("hex");

  return queryHash;
}

export function isSubscriptionPremium(
  subscription: ISubscriptionData
): boolean {
  const isPremium = Boolean(
    subscription.user || subscription.team || subscription.org
  );
  return isPremium;
}

export function getSubscriptionType(
  subscription: ISubscriptionData
): SubscriptionPlan {
  if (subscription.org) return SubscriptionPlan.ENTERPRISE;
  if (subscription.team) return SubscriptionPlan.TEAM;

  // Check for user subscription with metadata to determine specific tier
  if (subscription.user) {
    const tierMetadata = subscription.user.plan?.metadata?.type?.toLowerCase();

    if (tierMetadata === "deep") {
      return SubscriptionPlan.DEEP;
    }
    // Default to Premium for any other user subscription
    return SubscriptionPlan.PREMIUM;
  }

  return SubscriptionPlan.FREE;
}

export function isSubscriptionTeams(
  subscription: Stripe.Subscription
): Stripe.SubscriptionItem | null {
  if (subscription.items.data.length > 0) {
    for (let data of subscription.items.data) {
      if (data.plan.tiers_mode === "volume") {
        return data;
      }
    }
  }
  return null;
}

export function isSubscriptionApi(
  subscription: Stripe.Subscription
): Stripe.SubscriptionItem | null {
  if (subscription.items.data.length > 0) {
    for (let data of subscription.items.data) {
      if (
        data.plan.metadata &&
        data.plan.metadata.type === "api" &&
        data.price.recurring &&
        data.price.recurring.usage_type === "metered"
      ) {
        return data;
      }
    }
  }
  return null;
}

export function getSubscriptionPriceId(
  subscription: Stripe.Subscription
): string | null {
  if (subscription.items.data.length > 0) {
    for (let data of subscription.items.data) {
      return data.price.id;
    }
  }
  return null;
}

export function isSubscriptionPaused(subscription: any) {
  if (subscription && subscription.pause_collection != null) {
    return true;
  }
  return false;
}

export function isSubscriptionPausedAndNotPremium(subscription: any) {
  if (!isSubscriptionPaused(subscription)) {
    return false;
  }
  const pauseWallStartDate = subscription.metadata?.pauseWallStartDate;
  return pauseWallStartDate && new Date(pauseWallStartDate * 1000) < new Date();
}

export function isSubscriptionActive(subscription: any) {
  if (subscription && !["active", "trialing"].includes(subscription.status)) {
    return false;
  } else if (subscription.cancel_at_period_end) {
    if (Date.now() > subscription.cancel_at * 1000) {
      return false;
    } else {
      return true;
    }
  } else if (isSubscriptionPausedAndNotPremium(subscription)) {
    return false;
  } else {
    return true;
  }
}

export function isBillingAdminSubscription(subcription?: UserSubscription) {
  return (
    subcription?.plan.metadata?.type?.toLowerCase() ===
    SubscriptionPlan.TEAM.toLowerCase()
  );
}

/**
 * Type definition for subscription tiers
 */
export type SubscriptionTier =
  | "free"
  | "premium"
  | "deep"
  | "team"
  | "enterprise";

/**
 * Get the subscription tier for a user based on their subscription data
 * Safely handles unknown metadata types by falling back to appropriate defaults
 */
export const getSubscriptionTier = (subscription: any): SubscriptionTier => {
  if (!subscription) return "free";

  // Check for organization subscription first (Enterprise)
  if (subscription.org) return "enterprise";

  // Check for team subscription
  if (subscription.team) return "team";

  // Check for user subscription with metadata
  if (subscription.user) {
    const tierMetadata = subscription.user.plan?.metadata?.type?.toLowerCase();

    // Only return metadata type if it's a valid SubscriptionTier
    if (tierMetadata) {
      switch (tierMetadata) {
        case "premium":
          return "premium";
        case "deep":
          return "deep";
        case "team":
          return "team";
        case "enterprise":
          return "enterprise";
        // Handle edge cases - unknown metadata types fall through to legacy fallback
        case "lifetime":
        case "early bird":
        case "api":
        default:
          // Default to premium for unknown user subscription types
          return "premium";
      }
    }
    // User subscription without metadata - assume premium
    return "premium";
  }

  return "free";
};

/**
 * Check if a subscription tier is a paid tier
 */
export const isPaidTier = (tier: SubscriptionTier): boolean => {
  return tier !== "free";
};

/**
 * Get the display name for a subscription tier
 */
export const getSubscriptionTierDisplayName = (
  tier: SubscriptionTier
): string => {
  const displayNames: Record<SubscriptionTier, string> = {
    free: "Free",
    premium: "Pro",
    deep: "Deep",
    team: "Team",
    enterprise: "Enterprise",
  };

  return displayNames[tier] || "Free";
};
